// In production the frontend and API are served from the same origin. Using a
// relative URL avoids pointing the user's browser at its own localhost and
// also avoids mixed-content/CORS problems when the site is HTTPS.
const RAW_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const DEFAULT_BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8001';
const CONFIGURED_BACKEND_URL = RAW_BACKEND_URL || DEFAULT_BACKEND_URL;

// Ensure URL has protocol (http:// or https://)
// This prevents URL being treated as relative path
const BACKEND_URL = CONFIGURED_BACKEND_URL
  ? (CONFIGURED_BACKEND_URL.match(/^https?:\/\//)
    ? CONFIGURED_BACKEND_URL
    : `http://${CONFIGURED_BACKEND_URL}`)
  : '';

class ApiService {
  constructor() {
    this.baseURL = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

    // Log warning if URL was missing protocol (dev aid)
    if (RAW_BACKEND_URL && RAW_BACKEND_URL !== BACKEND_URL) {
      console.warn(`[ApiService] REACT_APP_BACKEND_URL missing protocol, using: ${BACKEND_URL}`);
    }
  }

  /**
   * Single read of response body: use text() then parse.
   * Avoids "body stream already read" and handles empty/non-JSON (e.g. 404 HTML).
   * Authentication travels exclusively via the httpOnly `session_token` cookie.
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const config = {
      ...options,
      headers,
      credentials: 'include',
    };

    let response;
    try {
      response = await fetch(url, config);
    } catch (error) {
      throw new Error(`Network error: ${error.message}`);
    }

    // Clone response if needed, but we'll read it only once
    let text = '';
    try {
      text = await response.text();
    } catch (error) {
      throw new Error(`Failed to read response: ${error.message}`);
    }

    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      if (response.ok) {
        throw new Error('Invalid JSON response');
      }
      // If it's not JSON, use the text as error message
      data = { error: text?.slice(0, 200) || 'Request failed' };
    }

    if (!response.ok) {
      const msg = data.error || data.message || `Request failed (${response.status})`;
      throw new Error(typeof msg === 'string' ? msg : 'Request failed');
    }

    return data;
  }

  // Auth
  async signup(data) {
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async requestPasswordRecovery(email) {
    return this.request('/auth/password-recovery/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, password) {
    return this.request('/auth/password-recovery/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async googleCallback(sessionId) {
    return this.request('/auth/google/callback', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Users
  async getUsers(options = {}) {
    const { page = 1, limit = 10, search = '', role = '' } = options;
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (search) params.append('search', search);
    if (role) params.append('role', role);
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/users${queryString}`);
  }

  async createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async toggleUser(id) {
    return this.request(`/users/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  // Services
  async getServices(options = {}) {
    const { page = 1, limit = 10, search = '' } = options;
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (search) params.append('search', search);
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/services${queryString}`);
  }

  async createService(data) {
    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateService(id, data) {
    return this.request(`/services/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async toggleService(id) {
    return this.request(`/services/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async executeService(id) {
    return this.request(`/services/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async deleteService(id) {
    return this.request(`/services/${id}`, { method: 'DELETE' });
  }

  // Products
  async getProducts(options = {}) {
    const { page = 1, limit = 10, search = '' } = options;
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (search) params.append('search', search);
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/products${queryString}`);
  }

  async createProduct(data) {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id, data) {
    return this.request(`/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async toggleProduct(id) {
    return this.request(`/products/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async deleteProduct(id) {
    return this.request(`/products/${id}`, { method: 'DELETE' });
  }

  // Invoices
  async getInvoices(queryString = '') {
    return this.request(`/invoices${queryString}`);
  }

  async createInvoice(data) {
    return this.request('/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async payInvoice(id) {
    return this.request(`/invoices/${id}/pay`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async cancelInvoice(id) {
    return this.request(`/invoices/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Expenses
  async getExpenses() {
    return this.request('/expenses');
  }

  async createExpense(data) {
    return this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteExpense(id) {
    return this.request(`/expenses/${id}`, { method: 'DELETE' });
  }

  // Donations
  async getDonations() {
    return this.request('/donations');
  }

  async createDonation(data) {
    return this.request('/donations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PDV
  async getPdvProducts() {
    return this.request('/pdv/products');
  }

  async getPdvServices() {
    return this.request('/pdv/services');
  }

  async getPdvCustomers() {
    return this.request('/pdv/customers');
  }

  async getPdvTransactions() {
    return this.request('/pdv');
  }

  async createPdvTransaction(data) {
    return this.request('/pdv', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelPdvTransaction(id) {
    return this.request(`/pdv/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // PDV Locations
  async getPdvLocations(options = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value.toString());
    });
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/pdv-locations${queryString}`);
  }

  async createPdvLocation(data) {
    return this.request('/pdv-locations', { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePdvLocation(id, data) {
    return this.request(`/pdv-locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async togglePdvLocation(id) {
    return this.request(`/pdv-locations/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({}) });
  }

  async deletePdvLocation(id) {
    return this.request(`/pdv-locations/${id}`, { method: 'DELETE' });
  }

  // Agendas
  async getAgendas() {
    return this.request('/agendas');
  }

  async createAgenda(data) {
    return this.request('/agendas', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyDayVisits(date) {
    const params = date ? `?date=${encodeURIComponent(date)}` : '';
    return this.request(`/my-day/visits${params}`);
  }

  // Customers
  async getCustomers(options = {}) {
    const { page = 1, limit = 10, search = '' } = options;
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (search) params.append('search', search);
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/customers${queryString}`);
  }

  async createCustomer(data) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomer(id, data) {
    return this.request(`/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async toggleCustomer(id) {
    return this.request(`/customers/${id}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async deleteCustomer(id) {
    return this.request(`/customers/${id}`, { method: 'DELETE' });
  }

  // Territories
  async getTerritories(options = {}) {
    const { page = 1, limit = 10, search = '' } = options;
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (search) params.append('search', search);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/territories${queryString}`);
  }

  async createTerritory(data) {
    return this.request('/territories', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTerritory(id, data) {
    return this.request(`/territories/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async toggleTerritory(id) {
    return this.request(`/territories/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({}) });
  }

  async deleteTerritory(id) {
    return this.request(`/territories/${id}`, { method: 'DELETE' });
  }

  // Customer Territories
  async getCustomerTerritories(options = {}) {
    const { page = 1, limit = 10, search = '', customerId = '', territoryId = '' } = options;
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    if (search) params.append('search', search);
    if (customerId) params.append('customerId', customerId);
    if (territoryId) params.append('territoryId', territoryId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/customer-territories${queryString}`);
  }

  async createCustomerTerritory(data) {
    return this.request('/customer-territories', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateCustomerTerritory(id, data) {
    return this.request(`/customer-territories/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async toggleCustomerTerritory(id) {
    return this.request(`/customer-territories/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({}) });
  }

  async deleteCustomerTerritory(id) {
    return this.request(`/customer-territories/${id}`, { method: 'DELETE' });
  }

  // Visit Configs
  async getVisitConfigs(options = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value.toString());
    });
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/visit-configs${queryString}`);
  }

  async createVisitConfig(data) {
    return this.request('/visit-configs', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateVisitConfig(id, data) {
    return this.request(`/visit-configs/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async toggleVisitConfig(id) {
    return this.request(`/visit-configs/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({}) });
  }

  async deleteVisitConfig(id) {
    return this.request(`/visit-configs/${id}`, { method: 'DELETE' });
  }

  // Stock
  async getStocks(options = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value.toString());
    });
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/stock${queryString}`);
  }

  async createStock(data) {
    return this.request('/stock', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateStock(id, data) {
    return this.request(`/stock/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async toggleStock(id) {
    return this.request(`/stock/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({}) });
  }

  async deleteStock(id) {
    return this.request(`/stock/${id}`, { method: 'DELETE' });
  }

  async getStockItems(options = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value.toString());
    });
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/stock/items${queryString}`);
  }

  async getStockMovements(options = {}) {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value.toString());
    });
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/stock/movements${queryString}`);
  }

  async createStockMovement(data) {
    return this.request('/stock/movements', { method: 'POST', body: JSON.stringify(data) });
  }

  async lookupPostalCode(postalCode) {
    return this.request(`/postal-code/${postalCode}`);
  }

  // Public onboarding
  async startOnboarding(data) {
    return this.request('/onboarding/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async lookupOnboardingPostalCode(postalCode) {
    return this.request(`/onboarding/postal-code/${postalCode}`);
  }

  async saveOnboardingCustomer(sessionToken, data) {
    return this.request(`/onboarding/${sessionToken}/customer`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getOnboardingServicePlans(sessionToken) {
    return this.request(`/onboarding/${sessionToken}/service-plans`);
  }

  async selectOnboardingPlan(sessionToken, servicePlanId) {
    return this.request(`/onboarding/${sessionToken}/select-plan`, {
      method: 'POST',
      body: JSON.stringify({ servicePlanId }),
    });
  }

  async submitOnboarding(sessionToken) {
    return this.request(`/onboarding/${sessionToken}/submit`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Legacy itinerant stock aliases
  async getItinerantStockLocations(options = {}) {
    return this.getStocks(options);
  }

  async createItinerantStockLocation(data) {
    return this.createStock(data);
  }

  async updateItinerantStockLocation(id, data) {
    return this.updateStock(id, data);
  }

  async toggleItinerantStockLocation(id) {
    return this.toggleStock(id);
  }

  async deleteItinerantStockLocation(id) {
    return this.deleteStock(id);
  }

  async getItinerantStockItems(options = {}) {
    return this.getStockItems(options);
  }

  async getItinerantStockMovements(options = {}) {
    return this.getStockMovements(options);
  }

  async createItinerantStockMovement(data) {
    return this.createStockMovement(data);
  }

  // Invoices Dashboard
  async getInvoicesDashboard(month) {
    const params = month ? `?month=${month}` : '';
    return this.request(`/invoices/dashboard${params}`);
  }

  // Dashboard Stats
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }
}

export default new ApiService();
